<template>
  <div id="page" class="container-fluid">

  <div class="p-2">
    <div class="container-fluid">
      <div class="row header mx-4 my-4">
        <div class="col dashboard-header">
          <h1>Employer Dashboard</h1>
          <img class="employer-logo" :src="employer.logoUrl">
        </div></div>
        <div class="col">
          <b-alert v-if="generalErrorMessage" :show="true" fade variant="danger">
            {{ generalErrorMessage }}
          </b-alert>
        </div>
      <div class="row payee-summary-header py-2">
        <div class="col-6">
          <h5>SUMMARY</h5>
        </div>
        <div class="col-6">
          <h5>ACCOUNTS</h5>
        </div>
      </div>
      <div class="row payee-summary py-2">
        <div class="col-6">
          <b-skeleton-wrapper :loading="isLoading">
            <template #loading>
              <span class="field-label">Name: </span><b-skeleton width="35%"></b-skeleton><br>
            </template>

            <span class="field-label">Name: </span><span class="account-text">{{ employer.name }}</span><br>
          </b-skeleton-wrapper>
        </div>
        <div class="col-6">
          <div class="mb-2" v-for="account in employer.accounts" :key="account.organizationAccountId">
                <span class="field-label">{{ account.name }}: </span> <span class="account-text mr-2">(Ends with {{ account.last4 }}) </span>
                  <span v-if="account.status === 'Verified'" class="pt-2"><font-awesome-icon :icon="[ 'far', 'check-circle']" class="orange mr-1"/><b>Verified</b> </span>
                  <span v-else><button @click.prevent="showAccountVerificationModal(account)" class="btn btn-outline-orange">Verify</button></span>
          </div>
          <div class="mb-2">
          <button @click.prevent="addAccount()" class="btn btn-outline-orange m-1 mt-2">
            <font-awesome-icon class="orange mr-1" icon="plus"/>
            <font-awesome-icon class="orange mr-1" :icon="['far', 'building-columns']"/>
            Account
          </button>
          </div>
        </div>
      </div>
    </div>
      <div class="row mt-2">
        <div class="col m-3">
          <h5>Episodes</h5>
        </div>
      </div>
      <div class="container-fluid">
      <div class="row data-table-header">
        <div class="col-1">Date</div>
        <div class="col-1">Status</div>
        <div class="col-2">Patient</div>
        <div class="col-2">Episode Name</div>
        <div class="col-1">Episode Term</div>
        <div class="col-2">Amount</div>
        <div class="col-1">Funding Status</div>
        <div class="col-2"></div>
      </div>
        <div class="row data-table-row" v-for="distribution in employer.paymentDistributions" :key="distribution.organizationBundlePaymentDistributionId">
          <div class="col-12">
            <div class="row align-items-center">
              <div class="column align-middle col-1">{{ formatDate(distribution.createdAt) }}</div>
              <div class="column align-middle col-1">{{ distribution.status }}</div>
              <div class="column align-middle col-2">{{ patientName(distribution) }}</div>
              <div class="column align-middle col-2">{{ distribution.bundle.name }}</div>
              <div class="column align-middle col-1">{{ formatTerm(distribution.bundle.durationDays) }}</div>
              <div class="column align-middle col-2"><strong>Total Episodic Fee:</strong> ${{formatNumber(distribution.price)}}
                <span v-if="distribution.estimate.patientAmount >0">
                  <br><strong>Patient Responsibility:</strong> ${{formatNumber(distribution.estimate.patientTotal)}}
                  <br><strong>Funding Amount:</strong> ${{formatNumber(distribution.maxFundingAmount)}}
                </span>
              </div>
              <div v-if="distribution.status === 'Approved' || distribution.status === 'Closed'" class="column align-middle col-1"><strong>Paid:</strong> ${{ formatNumber(-distribution.fundingAmount) }}</div>
              <div v-if="distribution.status === 'Pending'" class="column align-middle col-1">Pending</div>
              <div class="column col-2 text-right" v-if="distribution.status === 'Approved' || distribution.status === 'Closed' && distribution.fundingAmount">
                <template>
                  <button @click.prevent="showInvoice(distribution)" class="btn btn-outline-orange m-1">
                    <font-awesome-icon class="orange mr-1" :icon="['far', 'file-invoice-dollar']"/> Download Invoice
                  </button>
                </template>
              </div>
              <div class="column col-2 text-right" v-if="distribution.status === 'Pending'">
                <template>
                  <button @click.prevent="setDistributionApproval(distribution, true)" class="btn btn-outline-orange m-1">
                    <font-awesome-icon class="orange mr-1" :icon="['far', 'check']"/> Approve
                  </button>
                  <button @click.prevent="setDistributionApproval(distribution, false)" class="btn btn-outline-orange m-1">
                    <font-awesome-icon class="orange mr-1" :icon="['far', 'x']"/> Deny
                  </button>
                </template>
              </div>
            </div>
          </div>
        </div>
        <div v-if="employer.paymentDistributions && employer.paymentDistributions.length === 0" role="alert" class="alert alert-secondary">
          No episodes on file.
        </div>
      </div>
    <pdf-view-modal :title="pdfTitle" :url="pdfUrl" :visible="showingPdfModal"
                    @update:visible="visible => showingPdfModal = visible"></pdf-view-modal>
    <account-modal :visible="showingAccountModal" :account-employer="employer" @update:visible="visible => showingAccountModal = visible" @accountSaved="accountSaved()"></account-modal>
    <confirm-modal @confirmed="approvalConfirmed" title="Approval Confirmation" class="align-middle"
                   confirmButton="Confirm"
                   :visible="showingConfirmModal"
                   @update:visible="visible => showingConfirmModal = false">
        <div class="row mb-3">
          <div class="col-2"></div>
          <div class="col-8 text-center">Please confirm that this person is eligible to receive this service.</div>
          <div class="col-2"></div>
        </div>
    </confirm-modal>
    <micro-deposit-verification-modal :visible="showingAccountDepositsModal" :account-employer="employer" :account="selectedAccount" @update:visible="visible => showingAccountDepositsModal = visible" @accountVerified="accountVerified()"></micro-deposit-verification-modal>
    <account-needs-verification-modal :visible="showingAccountVerificationModal" :account-employer="employer" :account="selectedAccount" @update:visible="visible => showingAccountVerificationModal = visible" @accountVerificationStarted="accountVerificationStarted()"></account-needs-verification-modal>
    <loading-modal :visible="showingLoadingModal" @update:visible="visible => showingLoadingModal = visible"></loading-modal>

  </div>
    <paginator :totalPages="totalPages" :currentPage="currentPage" @update:currentPage="newPage => currentPage = newPage"></paginator>
  </div>
</template>

<script>
  import api from '@/services/api'
  import ErrorsMixin from '@/mixins/ErrorsMixin'
  import moment from 'moment'
  import AccountModal from '@/components/AccountModal'
  import ConfirmModal from '@/components/ConfirmModal'
  import MicroDepositVerificationModal from '@/components/MicroDepositVerificationModal'
  import AccountNeedsVerificationModal from '@/components/AccountNeedsVerificationModal'
  import PdfViewModal from '../components/PdfViewModal'
  import LoadingModal from '@/components/LoadingModal'
  import AnalyticsMixin from '@/mixins/AnalyticsMixin'

  export default {
    name: 'EmployerDashboard',

    mixins: [ErrorsMixin, AnalyticsMixin],

    components: { AccountModal, ConfirmModal, MicroDepositVerificationModal, AccountNeedsVerificationModal, PdfViewModal, LoadingModal },

    props: {
      id: {
        default: 0
      }
    },

    data () {
      return {
        employer: {
          organizationEmployerId: 0,
          name: '',
          bundles: [],
          paymentDistributions: []
        },
        isLoading: false,
        showingAccountModal: false,
        showingConfirmModal: false,
        confirmingDistribution: {},
        showingAccountVerificationModal: false,
        showingPdfModal: false,
        showingAccountDepositsModal: false,
        showingLoadingModal: false,
        pdfUrl: '',
        pdfTitle: '',
        totalPages: 0,
        currentPage: 1,
        pageSize: 20,
        selectedAccount: {
          externalId: ''
        }
      }
    },

    watch: {
      // call again the method if the route changes
      $route: 'loadEmployer'
    },

    computed: {
      isOrgAdmin () {
        return this.$store.isOrgAdmin
      }
    },

    methods: {

      patientName (distribution) {
        if (distribution.estimate && distribution.estimate.patient) {
          return distribution.estimate.patient.firstName + ' ' + distribution.estimate.patient.lastName
        }

        return ''
      },

      addAccount () {
        this.showingAccountModal = true
      },

      accountSaved () {
        this.showingAccountModal = false
        this.loadEmployer()
      },

      accountVerified () {
        this.showingAccountDepositsModal = false
        this.selectedAccount = { externalId: '' }
        this.loadEmployer()
      },

      accountVerificationStarted () {
        this.showingAccountVerificationModal = false
        this.selectedAccount = { externalId: '' }
        this.loadEmployer()
      },

      showInvoice (distribution) {
        this.showingLoadingModal = true
        this.pdfTitle = 'Episode Invoice for ' + distribution.bundle.name
        api.get('/Dashboard/PaymentDistribution/' + distribution.organizationBundlePaymentDistributionId + '/Employer/Download', {
          responseType: 'blob',
          headers: { 'Content-Type': 'application/pdf' }
        }).then(response => {
          this.trackEvent('Distribution', 'Invoice', 'ResponseTime', response.config.requestDuration)
          this.trackEvent('Distribution', 'Invoice', 'Show', distribution.organizationBundlePaymentDistributionId)

          if (window.navigator && window.navigator.msSaveOrOpenBlob) {
            window.navigator.msSaveOrOpenBlob(response.data, 'EmployerInvoice.pdf')
          } else {
            this.pdfUrl = URL.createObjectURL(response.data)
            this.showingPdfModal = true
          }
        }).catch(error => {
          if (!this.$store.canHandleNetworkError(error)) {
            this.trackEvent('Distribution', 'Invoice', error.message)
            this.$log.error('Error attempting to load invoice: ' + error.message, error)
            this.errors = ['Error loading invoice: ' + error.message]
          }
        }).then(() => {
          // always executed
          this.showingLoadingModal = false
        })
      },

      showAccountVerificationModal (account) {
        this.selectedAccount = account

        if (account.microDepositStatus) {
          this.isLoading = true

          const request = {
            fundingSourceId: account.externalId
          }

          api.post('/Dashboard/Employer/Accounts/VerificationDetails', request).then(response => {
            this.isLoading = false
            account.microDepositStatus = response.data.status

            if (account.microDepositStatus === 'Processed') { // deposits are complete, ready to verify
              this.showingAccountDepositsModal = true
            } else if (account.microDepositStatus === 'Pending') {
              this.showingAccountDepositsModal = true
            } else {
              this.showingAccountVerificationModal = true
            }
          }).catch(error => {
            this.isLoading = false
            if (!this.$store.canHandleNetworkError(error)) {
              this.setFromApiErrorResponse(error)
              this.$log.error('Error attempting to load employer verification details: ' + error.message, error)
            }
          })
        } else {
          this.showingAccountVerificationModal = true
        }
      },

      loadEmployer () {
        this.isLoading = true

        const parameters = {
          pageSize: this.pageSize,
          page: this.currentPage
        }

        api.post('/Dashboard/EmployerDistributions', parameters).then(response => {
          this.employer = response.data.employer
          this.currentPage = response.data.page
          this.totalPages = Math.ceil(response.data.totalDistributions / this.pageSize)
          this.isLoading = false
        }).catch(error => {
          this.isLoading = false
          if (!this.$store.canHandleNetworkError(error)) {
            this.setFromApiErrorResponse(error)
            this.$log.error('Error attempting to load employer details: ' + error.message, error)
          }
        })
      },

      approvalConfirmed (confirmed) {
        this.showingConfirmModal = false
        if (confirmed) {
          this.setDistributionApproval(this.confirmingDistribution, true, true)
        }
      },

      setDistributionApproval (distribution, approved, confirmed = false) {
        this.clearAllErrors()

        if (approved && !confirmed) {
          this.confirmingDistribution = distribution
          this.showingConfirmModal = true
          return
        }

        const status = approved ? 'Approved' : 'Denied'
        api.post('/Dashboard/PaymentDistribution/' + distribution.organizationBundlePaymentDistributionId + '/Status/' + status).then(response => {
          this.loadEmployer()
        }).catch(error => {
          this.isLoading = false
          if (!this.$store.canHandleNetworkError(error)) {
            this.setFromApiErrorResponse(error)
            this.$log.error('Error attempting to set payment distribution status: ' + error.message, error)
          }
        })
      },

      formatDate (date) {
        if (date) {
          return moment(date).format('MM/DD/YYYY')
        } else {
          return ''
        }
      },

      formatNumber (number) {
        if (number === 0 || number === 0.0) {
          return '0.00'
        }
        if (number) {
          return number.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,')
        }

        return ''
      },

      formatTerm (days) {
        if (days === 0) {
          return 'DOS'
        } else {
          return days + 'D'
        }
      }
    },

    created () {
      this.loadEmployer()
    }
  }
</script>

<style scoped>

.payee-summary-header {
  border-top: 1px solid #FFA800;
  background-color: #f9f9f9;
}

.payee-summary-header h5 {
  color: #001859;
}

.payee-summary .column {
  padding: 0.75rem;
  vertical-align: bottom;
  border-collapse: collapse;
}

.payee-summary {
  border-bottom: 1px solid #dee2e6;
  background-color: #f9f9f9;
  /*cursor: pointer;*/
}

.header-row div {
  padding: 0.75rem;
  vertical-align: bottom;
  border-top: 1px solid #dee2e6;
  border-bottom: 2px solid #dee2e6;
  font-weight: bold;
  border-collapse: collapse;
}

.data-row {
  min-height: 60px;
}

.b-skeleton-text {
  display: inline-block;
  vertical-align: middle;
}

.account-text {
  font-size: 20px;
  font-weight: 400;
}

.field-label {
  font-size: 16px;
}
 .dashboard-header {
   display: flex;
   align-items: center;
   justify-content: space-between;
 }

.employer-logo {
  max-height: 150px;
  max-width: 150px;
}
</style>
